/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { useEffect } from "react";

import { isQueryParamLocaleMode } from "../../utils/locale-path.utils";
import { reloadIntoCookieLocale, startLocaleSync } from "../../utils/locale-sync.utils";

import type { LocalePrefixMode } from "../../types/locale.types";

export interface LocaleTabSyncProps {
	/**
	 * The locale this page was rendered with (server-resolved, e.g. from the route
	 * segment). Used as the comparison baseline — a sync signal for a *different*
	 * locale triggers a reload. Pass the same `locale` the layout renders content with.
	 */
	locale: string;
	/** Per-site override for apps that serve tenants with different locale strategies. */
	localePrefix?: LocalePrefixMode;
}

/**
 * Reloads this tab when the `EPIC_LOCALE` cookie changes to a different locale —
 * via the Cookie Store API `change` event, which fires in every same-origin document
 * regardless of which tab (or response) set the cookie — so server-rendered content
 * re-renders in the new locale. Mounted once per page (in the localized layout,
 * alongside `GlobalErrorHandler`); renders nothing.
 *
 * Mount only on sites configured with query-param locale mode
 * (`NEXT_PUBLIC_LOCALE_MODE=never`), where the locale is cookie-driven and a reload
 * picks up the new value. In path-prefix modes the URL pins the locale, so a reload
 * could never change it — as a safety net the component also no-ops at runtime when
 * the mode is not "never", but that gate is not a reason to mount it elsewhere.
 *
 * The reload ({@link reloadIntoCookieLocale}) strips `?lang`/`?setlang` first:
 * middleware gives those params precedence over the `EPIC_LOCALE` cookie, so keeping
 * them would pin the tab to the old locale forever (reloading again on every
 * subsequent change).
 *
 * The comparison baseline is the `locale` the page was *rendered* with — not the live
 * cookie — because the cookie can differ from the displayed locale (middleware resolves
 * locale from `?lang=` / `Accept-Language` / path, and normalizes legacy cookie codes).
 *
 * All listener wiring and loop-prevention lives in {@link startLocaleSync}; this
 * component only binds it to the React lifecycle and injects the reload side effect.
 */
export function LocaleTabSync({ locale, localePrefix }: LocaleTabSyncProps): null {
	useEffect(() => {
		if (!isQueryParamLocaleMode(localePrefix)) {
			return undefined;
		}
		return startLocaleSync(locale, reloadIntoCookieLocale);
	}, [locale, localePrefix]);

	return null;
}
